import { numberFormat } from '../shared/utils'

type UnitTypeOptions =
  | 'g CO₂'
  | 'kg CO₂'
  | 't CO₂'
  | 'ct'
  | 'CENT'
  | 'EUR'
  | '€'
  | 'k €'
  | 'k€'
  | 'M €'
  | 'M€'
  | 'W'
  | 'kW'
  | 'MW'
  | 'GW'
  | 'Wh'
  | 'kWh'
  | 'MWh'
  | 'GWh'
  | 'EUR/MWh'

type UnitType = UnitTypeOptions | (string & {})

/**
 * Formats a numerical value by interpreting the input based on the specified unit and converting it to a readable format with appropriate unit scaling.
 *
 * @param {number} value - The numerical value to be formatted, could be in any specified unit.
 * @param {UnitType} unitType - The unit type of the input value. Determines how the value should be treated and converted.
 * @param {number} decimals - The number of decimal places to round the formatted value to. Defaults to 2.
 * @returns {string} The formatted number with its respective unit.
 *
 * @example
 * // Formats a power value provided in watts into a more readable format based on its size.
 * console.log(formatMeasurement(1500, 'W')); // "1.50 kW"
 *
 * @example
 * // Formats a currency amount in euros, adjusting for different scales (cent, euro, kilo-euros).
 * console.log(formatMeasurement(0.75, '€', 2)); // "0.75 €"
 * console.log(formatMeasurement(1250, '€', 0)); // "1 k €"
 *
 * @example
 * // Formats an energy consumption value provided in kilowatt-hours to an appropriate scale.
 * console.log(formatMeasurement(5000, 'kWh')); // "5.00 MWh"
 *
 * @example
 * // Formats CO₂ emissions given in grams to appropriate larger units if necessary.
 * console.log(formatMeasurement(2000000, 'g CO₂')); // "2.00 t CO₂"
 */
export const formatMeasurement = (value: number | null | undefined, unitType: UnitType, decimals = 2) => {
  if (value === undefined || value === null) return '-'
  let baseValue: number
  // Convert input to its base unit
  switch (unitType) {
    case 'g CO₂':
      baseValue = value
      break
    case 'kg CO₂':
      baseValue = value * 1000 // Convert kg to g
      break
    case 't CO₂':
      baseValue = value * 1000000 // Convert tonnes to g
      break
    case 'ct':
      baseValue = value / 100 // Convert cents to euros
      break
    case 'CENT':
      baseValue = value / 100 // Convert CENT to euros
      break
    case 'EUR':
      baseValue = value
      break
    case '€':
      baseValue = value
      break
    case 'k €':
      baseValue = value * 1000 // Convert thousands of euros to euros
      break
    case 'k€':
      baseValue = value * 1000 // Convert thousands of euros to euros
      break
    case 'M €':
      baseValue = value * 1000000 // Convert millions of euros to euros
      break
    case 'M€':
      baseValue = value * 1000000 // Convert millions of euros to euros
      break
    case 'W':
      baseValue = value / 1000 // Convert watts to kilowatts
      break
    case 'kW':
      baseValue = value
      break
    case 'MW':
      baseValue = value * 1000 // Convert megawatts to kilowatts
      break
    case 'GW':
      baseValue = value * 1000000 // Convert gigawatts to kilowatts
      break
    case 'Wh':
      baseValue = value / 1000 // Convert watt-hours to kilowatt-hours
      break
    case 'kWh':
      baseValue = value
      break
    case 'MWh':
      baseValue = value * 1000 // Convert megawatt-hours to kilowatt-hours
      break
    case 'GWh':
      baseValue = value * 1000000 // Convert gigawatt-hours to kilowatt-hours
      break
    case 'TWh':
      baseValue = value * 1000000000 // Convert terawatt-hours to kilowatt-hours
      break
    case 'EUR/MWh': // Added case for EUR/MWh to ct/kWh conversion
      baseValue = value / 10 // Convert EUR/MWh to ct/kWh
      break
    default: // Default case if no conversion is necessary
      return `${numberFormat(value, decimals)} ${unitType}`
  }

  // Determine the appropriate unit and format for the output
  return formatBasedOnValueAndType(baseValue, unitType, decimals)
}

/**
 * Determines the appropriate scaling and unit based on the converted base value.
 *
 * @param {number} baseValue - The value converted to its base unit.
 * @param {UnitType} originalUnitType - The original unit type provided by the user.
 * @param {number} decimals - Number of decimal places for rounding.
 * @returns {string} Formatted value with unit.
 */
function formatBasedOnValueAndType(baseValue: number, originalUnitType: UnitType, decimals: number): string {
  let formattedNumber: number
  let unit: string
  const baseValueAbs = Math.abs(baseValue)
  const isCurrency =
    originalUnitType.includes('€') || originalUnitType === 'EUR' || originalUnitType === 'CENT' || originalUnitType === 'ct'

  // Special case for energy price formatting
  if (originalUnitType === 'EUR/MWh') {
    formattedNumber = baseValue
    unit = 'ct/ kWh'
  } else if (originalUnitType.includes('CO₂')) {
    // Logic to format number based on its base value and type
    if (baseValueAbs < 1000) {
      formattedNumber = baseValue
      unit = 'g CO₂'
    } else if (baseValueAbs < 1000000) {
      formattedNumber = baseValue / 1000
      unit = 'kg CO₂'
    } else {
      formattedNumber = baseValue / 1000000
      unit = 't CO₂'
    }
  } else if (isCurrency) {
    if (baseValueAbs < 1) {
      // For CENT input, we want to show 0.xx € (not "ct") below 1€
      if (originalUnitType === 'CENT') {
        formattedNumber = baseValue
        unit = '€'
      } else {
        formattedNumber = baseValue * 100
        unit = 'ct'
      }
    } else if (baseValueAbs < 1000) {
      formattedNumber = baseValue
      unit = '€'
    } else if (baseValueAbs < 1000000) {
      formattedNumber = baseValue / 1000
      unit = 'k €'
    } else {
      formattedNumber = baseValue / 1000000
      unit = 'M €'
    }
  } else if (originalUnitType.includes('Wh')) {
    if (baseValueAbs < 1) {
      formattedNumber = baseValue * 1000
      unit = 'Wh'
    } else if (baseValueAbs < 1000) {
      formattedNumber = baseValue
      unit = 'kWh'
    } else if (baseValueAbs < 1000000) {
      formattedNumber = baseValue / 1000
      unit = 'MWh'
    } else if (baseValueAbs < 1000000000) {
      formattedNumber = baseValue / 1000000
      unit = 'GWh'
    } else {
      formattedNumber = baseValue / 1000000000
      unit = 'TWh'
    }
  } else if (originalUnitType.includes('W')) {
    if (baseValueAbs < 1) {
      formattedNumber = baseValue * 1000
      unit = 'W'
    } else if (baseValueAbs < 1000) {
      formattedNumber = baseValue
      unit = 'kW'
    } else if (baseValueAbs < 1000000) {
      formattedNumber = baseValue / 1000
      unit = 'MW'
    } else if (baseValueAbs < 1000000000) {
      formattedNumber = baseValue / 1000000
      unit = 'GW'
    } else {
      formattedNumber = baseValue / 1000000000
      unit = 'TW'
    }
  } else {
    // Default case if no specific formatting logic is required
    formattedNumber = baseValue
    unit = originalUnitType
  }

  return `${numberFormat(formattedNumber, decimals)} ${unit}`
}

// Examples using the formatMeasurement function with different scenarios

// CO₂ Units
// console.log("formatNumberWithUnit(500, 'g CO₂')", formatNumberWithUnit(500, 'g CO₂')) // Outputs: "500.00 g CO₂"
// console.log("formatNumberWithUnit(1500, 'kg CO₂')", formatNumberWithUnit(1500, 'kg CO₂')) // Outputs: "1.50 kg CO₂"
// console.log("formatNumberWithUnit(5000000, 't CO₂')", formatNumberWithUnit(5000000, 't CO₂')) // Outputs: "5.00 t CO₂"

// // Currency Units
// console.log("formatNumberWithUnit(375, 'ct')", formatNumberWithUnit(375, 'ct')) // Outputs: "75.00 ct"
// console.log("formatNumberWithUnit(2000000, '€')", formatNumberWithUnit(2000000, '€')) // Outputs: "999.00 €"
// console.log("formatNumberWithUnit(5, 'k €')", formatNumberWithUnit(5, 'k €')) // Outputs: "50.00 k €"
// console.log("formatNumberWithUnit(0.1, 'M €')", formatNumberWithUnit(0.1, 'M €')) // Outputs: "1.00 M €"

// // Power Units
// console.log("formatNumberWithUnit(999, 'W')", formatNumberWithUnit(999, 'W')) // Outputs: "0.999 kW"
// console.log("formatNumberWithUnit(1, 'kW')", formatNumberWithUnit(1, 'kW')) // Outputs: "1.00 kW"
// console.log("formatNumberWithUnit(500, 'MW')", formatNumberWithUnit(500, 'MW')) // Outputs: "500000.00 kW"
// console.log("formatNumberWithUnit(1, 'GW')", formatNumberWithUnit(1, 'GW')) // Outputs: "1000000.00 kW"

// // Energy Units
// console.log("formatNumberWithUnit(500, 'Wh')", formatNumberWithUnit(500, 'Wh')) // Outputs: "0.50 kWh"
// console.log("formatNumberWithUnit(1, 'kWh')", formatNumberWithUnit(1, 'kWh')) // Outputs: "1.00 kWh"
// console.log("formatNumberWithUnit(1000, 'MWh')", formatNumberWithUnit(1000, 'MWh')) // Outputs: "1000000.00 kWh"
// console.log("formatNumberWithUnit(1, 'GWh')", formatNumberWithUnit(1, 'GWh')) // Outputs: "1000000.00 kWh"

// First iteration with labelled units
// type UnitType = 'co2' | 'euro' | 'load' | 'consumption'
// /**
//  * Formats a numerical value by applying unit conversions based on the specified type and rounding to the specified number of decimal places.
//  * If no type is provided, it returns the number formatted to the specified number of decimal places without any unit.
//  *
//  * @param {number} value - The number to be formatted.
//  * @param {UnitType} [type] - The type of unit, which determines the conversion:
//  *  - `co2`: Expects input in grams (g). Formats to grams (g CO₂), kilograms (kg CO₂), or tonnes (t CO₂).
//  *  - `euro`: Expects input in euros (€). Formats to cents (ct), euros (€), thousand euros (k €), or million euros (M €).
//  *  - `load`: Expects input in kilowatts (kW). Formats to watts (W), kilowatts (kW), megawatts (MW), or gigawatts (GW).
//  *  - `consumption`: Expects input in kilowatt-hours (kWh). Formats to watt-hours (Wh), kilowatt-hours (kWh), megawatt-hours (MWh), or gigawatt-hours (GWh).
//  * @param {number} [decimals=2] - The number of decimal places to round the number to. Defaults to 2.
//  * @returns {string} The formatted number with its respective unit.
//  */
// export const formatNumberWithUnit = (value: number | null | undefined, type: UnitType, decimals = 2) => {
//   if (value === undefined || value === null) return '-'

//   let formattedNumber: number
//   let unit: string

//   switch (type) {
//     case 'co2':
//       formattedNumber = value < 1000 ? value : value < 1000000 ? value / 1000 : value / 1000000
//       unit = value < 1000 ? 'g CO₂' : value < 1000000 ? 'kg CO₂' : 't CO₂'
//       break
//     case 'euro':
//       formattedNumber = value < 1 ? value * 100 : value < 1000 ? value : value < 1000000 ? value / 1000 : value / 1000000
//       unit = value < 1 ? 'ct' : value < 1000 ? '€' : value < 1000000 ? 'k €' : 'M €'
//       break
//     case 'load':
//       formattedNumber = value < 1 ? value * 1000 : value < 1000 ? value : value < 1000000 ? value / 1000 : value / 1000000
//       unit = value < 1 ? 'W' : value < 1000 ? 'kW' : value < 1000000 ? 'MW' : 'GW'
//       break
//     case 'consumption':
//       formattedNumber = value < 1 ? value * 1000 : value < 1000 ? value : value < 1000000 ? value / 1000 : value / 1000000
//       unit = value < 1 ? 'Wh' : value < 1000 ? 'kWh' : value < 1000000 ? 'MWh' : 'GWh'
//       break
//   }

//   return `${numberFormat(formattedNumber, decimals)} ${unit}`
// }
